import { CheckCircle2, Clock, FileText, XCircle, type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PaymentStatusTotal } from "../../../../center";
import {
    type ChartTokens,
    formatCount,
    formatMoney,
    toAmount,
} from "../../../../../shared/components/reports";

/** Where the money is, first; where it went nowhere, last. */
const ORDER = ["paid", "pending", "draft", "cancelled"];

const ICONS: Record<string, LucideIcon> = {
    paid: CheckCircle2,
    pending: Clock,
    draft: FileText,
    cancelled: XCircle,
};

function statusColor(status: string, tokens: ChartTokens): string {
    switch (status) {
        case "paid":
            return tokens.success;
        case "pending":
            return tokens.warning;
        case "cancelled":
            return tokens.critical;
        default:
            return tokens.neutral;
    }
}

interface Props {
    statuses: PaymentStatusTotal[];
    tokens: ChartTokens;
}

/**
 * Every payment in the window by what became of it: how many, how much, and
 * each one's share of the money.
 *
 * Status colours are reserved for states, and each row carries its icon and
 * its name as well, so none is told apart by colour alone. The numbers are in
 * ink, not in the status colour.
 */
export default function PaymentStatusBreakdown({ statuses, tokens }: Props) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;

    const rows = [...statuses].sort((a, b) => {
        const rank = (s: string) => (ORDER.indexOf(s) === -1 ? ORDER.length : ORDER.indexOf(s));
        return rank(a.status) - rank(b.status);
    });
    const total = rows.reduce((sum, row) => sum + toAmount(row.amount), 0);

    return (
        <ul className="flex flex-col gap-4" aria-label={t("center.reports.byStatus")}>
            {rows.map((row) => {
                const Icon = ICONS[row.status] ?? FileText;
                const amount = toAmount(row.amount);
                const share = total > 0 ? (amount / total) * 100 : 0;
                const color = statusColor(row.status, tokens);
                return (
                    <li key={row.status}>
                        <div className="flex items-center justify-between gap-3 text-sm">
                            <span className="flex items-center gap-2 font-medium text-content-primary">
                                <Icon
                                    className="h-4 w-4 shrink-0"
                                    style={{ color }}
                                    aria-hidden="true"
                                />
                                {t(`center.payments.states.${row.status}`, {
                                    defaultValue: row.status,
                                })}
                            </span>
                            <span className="tabular-nums text-content-primary">
                                {formatMoney(amount, locale)}
                            </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-3">
                            <div
                                className="h-2 flex-1 overflow-hidden rounded-full bg-surface-sunken"
                                role="presentation"
                            >
                                <div
                                    className="h-full rounded-full"
                                    style={{ width: `${share}%`, backgroundColor: color }}
                                />
                            </div>
                            <span className="w-24 shrink-0 text-right text-xs tabular-nums text-content-secondary">
                                {t("center.reports.paymentCount", {
                                    count: row.count,
                                    formatted: formatCount(row.count, locale),
                                })}
                            </span>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
}
